import Select from '@quintype/em/components/select';
import { FieldLabel } from '@quintype/em/components/field-label';
import { TextArea } from '@quintype/em/components/text-area';
import { TextField } from '@quintype/em/components/text-field';
import get from 'lodash/get';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateBackupConfig, updateHaveUnsavedChanges } from '../../../../actions/builder-action';
import { updateGlobalAiElementSettings } from '../../../../actions/story-action';
import ActionButtons from '../../../../molecules/action-buttons';
import PBInspector from '../../../../molecules/inspector';
import { ToggleButton } from '../../../../molecules/toggle-button';
import { IConfig, Window } from '../../../../utils/interfaces';
import { Separator } from '../../../../atoms/separator';
import { shortCodeText } from '../../../../constant';
import { copyTextToClipboard } from '../../../../molecules/row-inspector/utils';
import { DUMMY_QUIZ_DATA, GAMETYPE_MODE_OPTIONS } from './constants';
import parentStyles from '../global-ai-elements-page.module.css';
import styles from './quiz.module.css';

interface IPBState {
  builder: {
    config: IConfig;
    haveUnsavedChanges: boolean;
  };
}

declare var window: Window;

export const Quiz = () => {
  const [showInspector, setShowInspector] = useState(false);
  const dispatch = useDispatch();
  const { config, haveUnsavedChanges } = useSelector((state: IPBState) => get(state, ['builder']));

  const quizConfig = get(config, ['story', 'globalAiElements', 'quiz'], {});
  const opts = get(quizConfig, ['opts'], {});

  const isEnabled = get(opts, ['enabled'], false);

  const updateOpts = (key: string, value: any) => {
    dispatch(updateGlobalAiElementSettings({ type: 'quiz', settingsType: 'opts', data: { [key]: value } }));
    dispatch(updateHaveUnsavedChanges(true));
  };

  const openInspector = () => {
    dispatch(updateBackupConfig({ config }));
    setShowInspector(true);
  };

  const QuizComponent = window?.QT_STORY_COMPONENTS?.GameType;

  const selectedMode =
    GAMETYPE_MODE_OPTIONS.find(o => o.value === get(opts, ['gameTypeMode'], 'staging')) || GAMETYPE_MODE_OPTIONS[0];

  return (
    <>
      <div className={parentStyles.sectionHeading} style={{ marginTop: 'calc(var(--pb-gutter-basic) * 4)' }}>
        Quiz
      </div>

      <div className={parentStyles.elementDetails}>
        <div className={parentStyles.templateWrapper}>
          <div className={parentStyles.hoverHeading}>Quiz</div>
          <div className={parentStyles.template}>
            {QuizComponent && (
              <QuizComponent
                element={DUMMY_QUIZ_DATA}
                opts={{
                  enabled: isEnabled,
                  previewScreen: 'quiz',
                  gameTypeKey: get(opts, ['gameTypeKey'], ''),
                  gameTypeMode: get(opts, ['gameTypeMode'], 'staging'),
                  startQuizMessage: get(
                    opts,
                    ['startQuizMessage'],
                    'Answer questions, earn points, and unlock a free subscription 🎁. Keep playing to collect more points.'
                  ),
                  startPlayingButton: get(opts, ['startPlayingButton'], 'Start Playing'),
                  nextQuestionButton: get(opts, ['nextQuestionButton'], 'Next Question'),
                  sendAsGiftButton: get(opts, ['sendAsGiftButton'], 'Send as Gift'),
                  sendGiftButton: get(opts, ['sendGiftButton'], 'Send Gift'),
                  claimButton: get(opts, ['claimButton'], 'Claim'),
                  recipientPhoneLabel: get(opts, ['recipientPhoneLabel'], "Enter recipient's email or phone number"),
                  totalPointsLabel: get(opts, ['totalPointsLabel'], 'Total Points'),
                  rewardUnlockedMessage: get(
                    opts,
                    ['rewardUnlockedMessage'],
                    'You did it! Your FREE 7-day subscription is now active. Start exploring premium articles and enjoy unlimited access.'
                  ),
                  giftSubscriptionMessage: get(
                    opts,
                    ['giftSubscriptionMessage'],
                    'You did it! Your FREE 7-day subscription is unlocked. Since you already have a subscription, you can gift it to someone or claim it later from your profile.'
                  ),
                  quizProgressMessage: get(
                    opts,
                    ['quizProgressMessage'],
                    "You're almost there!  🎯 You have [points-earned] points, just [points-remaining] more to unlock your reward. Play more quizzes to claim it!"
                  ),
                  rewardAlreadyUsedMessage: get(
                    opts,
                    ['rewardAlreadyUsedMessage'],
                    "You've already claimed the available reward. Keep playing quizzes to collect more points."
                  ),
                  quizAlreadyAttemptedMessage: get(
                    opts,
                    ['quizAlreadyAttemptedMessage'],
                    "You've already attempted this quiz. Try quizzes from other stories to earn more points."
                  ),
                  giftSentSuccessfullyMessage: get(opts, ['giftSentSuccessfullyMessage'], 'Gift sent successfully!'),
                  existingRewardMessage: get(
                    opts,
                    ['existingRewardMessage'],
                    '🎁 You already have an existing reward to claim/gift'
                  )
                }}
              />
            )}
          </div>
          <div className={parentStyles.buttonGroup}>
            <ActionButtons actionList={[{ action: 'setting', handler: openInspector }]} />
          </div>
        </div>
      </div>

      {showInspector && (
        <PBInspector
          title="GameType Quiz Settings"
          isActive={showInspector}
          isActionButtonDisabled={!haveUnsavedChanges}
          onActionButtonClick={() => setShowInspector(false)}
          onClose={() => setShowInspector(false)}
        >
          <ToggleButton
            label="Enable GameType Quiz"
            toggleName="enabled"
            id="quiz-enabled"
            updateToggleState={isEnabled}
            updateToggleButton={(name, checked) => updateOpts(name, checked)}
          />
          {isEnabled && (
            <>
              <TextField
                label="GameType Key"
                placeholder="Enter GameType Key"
                value={get(opts, ['gameTypeKey'], '')}
                onChange={(value: string) => updateOpts('gameTypeKey', value)}
              />
              <div className={styles.selectField}>
                <div className={styles.selectLabel}>GameType Mode</div>
                <Select
                  options={GAMETYPE_MODE_OPTIONS}
                  value={selectedMode}
                  onChange={(obj: any) => updateOpts('gameTypeMode', obj.value)}
                  placeholder="Select"
                />
              </div>
              <Separator />
              <TextField
                label="Start Playing Button"
                placeholder="Start Playing"
                value={get(opts, ['startPlayingButton'], 'Start Playing')}
                onChange={(value: string) => updateOpts('startPlayingButton', value)}
              />
              <TextField
                label="Next Question Button"
                placeholder="Next Question"
                value={get(opts, ['nextQuestionButton'], 'Next Question')}
                onChange={(value: string) => updateOpts('nextQuestionButton', value)}
              />
              <TextField
                label="Send As Gift Button"
                placeholder="Send as Gift"
                value={get(opts, ['sendAsGiftButton'], 'Send as Gift')}
                onChange={(value: string) => updateOpts('sendAsGiftButton', value)}
              />
              <TextField
                label="Send Gift Button"
                placeholder="Send Gift"
                value={get(opts, ['sendGiftButton'], 'Send Gift')}
                onChange={(value: string) => updateOpts('sendGiftButton', value)}
              />
              <TextField
                label="Claim Button"
                placeholder="Claim"
                value={get(opts, ['claimButton'], 'Claim')}
                onChange={(value: string) => updateOpts('claimButton', value)}
              />
              <TextField
                label="Enter recipient's email or phone number"
                placeholder="Enter recipient's email or phone number"
                value={get(opts, ['recipientPhoneLabel'], "Enter recipient's email or phone number")}
                onChange={(value: string) => updateOpts('recipientPhoneLabel', value)}
              />
              <TextField
                label="Total Points"
                placeholder="Total Points"
                value={get(opts, ['totalPointsLabel'], 'Total Points')}
                onChange={(value: string) => updateOpts('totalPointsLabel', value)}
              />
              <Separator />
              <TextArea
                label="Quiz Start Message"
                placeholder="Answer questions, earn points, and unlock a free subscription 🎁. Keep playing to collect more points."
                value={get(
                  opts,
                  ['startQuizMessage'],
                  'Answer questions, earn points, and unlock a free subscription 🎁. Keep playing to collect more points.'
                )}
                onChange={(value: string) => updateOpts('startQuizMessage', value)}
              />
              <TextArea
                label="Reward Unlocked Message"
                placeholder="You did it! Your FREE 7-day subscription is now active. Start exploring premium articles and enjoy unlimited access."
                value={get(
                  opts,
                  ['rewardUnlockedMessage'],
                  'You did it! Your FREE 7-day subscription is now active. Start exploring premium articles and enjoy unlimited access.'
                )}
                onChange={(value: string) => updateOpts('rewardUnlockedMessage', value)}
              />
              <TextArea
                label="Gift Subscription Message"
                placeholder="You did it! Your FREE 7-day subscription is unlocked. Since you already have a subscription, you can gift it to someone or claim it later from your profile."
                value={get(
                  opts,
                  ['giftSubscriptionMessage'],
                  'You did it! Your FREE 7-day subscription is unlocked. Since you already have a subscription, you can gift it to someone or claim it later from your profile.'
                )}
                onChange={(value: string) => updateOpts('giftSubscriptionMessage', value)}
              />
              <TextArea
                label="Quiz Progress Message"
                placeholder="You're almost there! 🎯 You have [points-earned] points, just [points-remaining] more to unlock your reward. Play more quizzes to claim it!"
                value={get(
                  opts,
                  ['quizProgressMessage'],
                  "You're almost there! 🎯 You have [points-earned] points, just [points-remaining] more to unlock your reward. Play more quizzes to claim it!"
                )}
                onChange={(value: string) => updateOpts('quizProgressMessage', value)}
              />
              <TextArea
                label="Reward Already Used Message"
                placeholder="You've already claimed the available reward. Keep playing quizzes to collect more points."
                value={get(
                  opts,
                  ['rewardAlreadyUsedMessage'],
                  "You've already claimed the available reward. Keep playing quizzes to collect more points."
                )}
                onChange={(value: string) => updateOpts('rewardAlreadyUsedMessage', value)}
              />
              <TextArea
                label="Quiz Already Attempted Message"
                placeholder="You've already attempted this quiz. Try quizzes from other stories to earn more points."
                value={get(
                  opts,
                  ['quizAlreadyAttemptedMessage'],
                  "You've already attempted this quiz. Try quizzes from other stories to earn more points."
                )}
                onChange={(value: string) => updateOpts('quizAlreadyAttemptedMessage', value)}
              />
              <TextArea
                label="Gift Sent Successfully Message"
                placeholder="Gift sent successfully!"
                value={get(opts, ['giftSentSuccessfullyMessage'], 'Gift sent successfully!')}
                onChange={(value: string) => updateOpts('giftSentSuccessfullyMessage', value)}
              />
              <TextArea
                label="You already have an existing reward to claim/gift"
                placeholder="🎁 You already have an existing reward to claim/gift"
                value={get(opts, ['existingRewardMessage'], '🎁 You already have an existing reward to claim/gift')}
                onChange={(value: string) => updateOpts('existingRewardMessage', value)}
              />
              <Separator />
              <div className={styles.shortCodeWrapper}>
                <FieldLabel label={'Available shortcodes'} />
                <div className={styles.hintText}>{shortCodeText}</div>
                <span className={styles.shortCode} onClick={() => copyTextToClipboard('[points-earned]')}>
                  Points Earned
                </span>
                <span className={styles.shortCode} onClick={() => copyTextToClipboard('[points-remaining]')}>
                  Points Remaining
                </span>
              </div>
            </>
          )}
        </PBInspector>
      )}
    </>
  );
};
